<ng-container *transloco="let t">
    <div
        *ngIf="chartOptions && data1"
        class="full-chart-page w-full h-full flex flex-col sai-ambient sai-ambient--grid"
    >
        <!-- Header Section -->
        <div class="fcp-header relative flex-shrink-0">
            <button
                *ngIf="data1.from === 'Tracker'"
                mat-icon-button
                (click)="closeWithState()"
                class="absolute top-2 right-2 z-10"
                [matTooltip]="t('Close')"
            >
                <mat-icon>close</mat-icon>
            </button>
            <div class="max-w-7xl mx-auto px-6 py-5">
                <!-- Tracker Header -->
                <ng-container *ngIf="data1.from === 'Tracker'">
                    <!-- Title + Close -->
                    <div class="mb-4">
                        <div class="text-center pr-10">
                        <h1 class="text-3xl font-bold text-gray-800 dark:text-gray-200 mb-3">
                            {{ chartOptions.name || t('Tracker Chart') }}
                        </h1>

                        <!-- Metadata Tags -->
                        <div
                            class="flex flex-wrap gap-2 items-center justify-center text-sm text-gray-600 dark:text-gray-300"
                        >
                            <span
                                *ngIf="chartOptions.metricColumns?.length"
                                class="inline-flex items-center gap-1"
                            >
                                <span class="font-semibold">{{ t('Metrics:') }}</span>
                                <span>{{
                                    chartOptions.metricColumns.join(", ")
                                }}</span>
                            </span>
                            <span
                                *ngIf="chartOptions.dateColumn"
                                class="inline-flex items-center gap-1"
                            >
                                <span class="font-semibold">•</span>
                            </span>
                            <span
                                *ngIf="chartOptions.dateColumn"
                                class="inline-flex items-center gap-1"
                            >
                                <span class="font-semibold">{{ t('Date Column:') }}</span>
                                <span>{{ chartOptions.dateColumn }}</span>
                            </span>
                        </div>
                        </div>
                    </div>
                </ng-container>

                <!-- Non-Tracker Header -->
                <ng-container *ngIf="data1.from !== 'Tracker'">
                    <div class="space-y-3">
                        <p class="text-lg text-gray-500 leading-5">
                            <span class="text-xl font-semibold text-gray-600 dark:text-gray-300"
                                >{{ t('Source Table:') }}</span
                            >
                            {{ chartOptions.tableName }}
                        </p>

                        <p class="text-lg text-gray-500 leading-6">
                            <span class="text-xl font-semibold text-gray-600 dark:text-gray-300"
                                >{{ t('Question:') }}</span
                            >
                            {{
                                chartOptions.questionId
                                    ? chartOptions.question
                                    : chartOptions.messageId
                                    ? chartOptions.message
                                    : ""
                            }}
                        </p>

                        <div
                            class="flex justify-between items-center gap-2 flex-wrap"
                        >
                            <div
                                [matTooltip]="chartOptions.agent.name"
                                class="py-1 px-4 rounded-full text-sm font-medium bg-primary line-clamp-1 max-w-[50%] overflow-hidden"
                            >
                                {{ chartOptions.agent.name }}
                            </div>
                            <div
                                class="py-1 px-4 rounded-full text-sm font-medium bg-secondary"
                            >
                                {{
                                    chartOptions.createdAt
                                        | date : "dd-MM-yyyy HH:mm"
                                }}
                            </div>
                        </div>
                    </div>
                </ng-container>
            </div>
        </div>

        <!-- Content Section -->
        <div class="flex-1 overflow-auto">
            <div class="w-full px-6 py-6">
                <!-- Tracker Content -->
                <ng-container
                    *ngIf="data1.from === 'Tracker'; else defaultFull"
                >
                    <div class="flex gap-6 flex-col lg:flex-row">
                        <!-- Chart Card -->
                        <div
                            class="fcp-card flex-1 min-w-0 lg:w-1/2"
                        >
                            <div class="p-6">
                                <!-- Table Name, Date Range Filter & Filter Button -->
                                <div
                                    class="flex flex-col lg:flex-row lg:justify-between lg:items-center w-full pt-3 mb-4 gap-3"
                                >
                                    <!-- Table Name & Data Source (soft badges — matches new-tracker card) -->
                                    <div
                                        class="flex flex-wrap items-center gap-1.5 sm:gap-2 min-w-0 justify-self-start ms-1"
                                    >
                                        <div
                                            class="fcp-meta-chip"
                                            [class.cursor-pointer]="trackerName"
                                            [matTooltip]="t('Data Table')"
                                            (click)="trackerName && openViewTableDialog()"
                                        >
                                            <mat-icon>table_chart</mat-icon>
                                            <span class="fcp-meta-text">
                                                {{
                                                    (trackerName ? trackerName : "")
                                                        | truncate : 20
                                                        | uppercase
                                                }}
                                            </span>
                                        </div>
                                        @if (trackerDs) {
                                            <button
                                                type="button"
                                                class="fcp-meta-source"
                                                [matTooltip]="t('Data Source')"
                                                (click)="
                                                    navigateToDataSource(
                                                        trackerDs
                                                    )
                                                "
                                            >
                                                <mat-icon>storage</mat-icon>
                                                <span class="fcp-meta-text">
                                                    {{
                                                        getDataSourceDisplayName(
                                                            trackerDs
                                                        ) | truncate : 20
                                                    }}
                                                </span>
                                            </button>
                                        }
                                    </div>

                                    <!-- Date Range Filter & Filter Button -->
                                    <div
                                        class="flex items-center gap-2 flex-wrap"
                                    >
                                        <!-- Date Range Display -->
                                        <div
                                            *ngIf="startDate || endDate"
                                            class="fcp-date-toolbar"
                                        >
                                            <span
                                                *ngIf="startDate"
                                                class="fcp-date-segment truncate min-w-0"
                                            >
                                                <span class="fcp-date-label">{{
                                                    t("From")
                                                }}</span>
                                                :
                                                {{
                                                    startDate
                                                        | date : "dd-MM-yyyy"
                                                }}
                                            </span>
                                            <span
                                                *ngIf="endDate"
                                                class="fcp-date-segment truncate min-w-0"
                                            >
                                                <span class="fcp-date-label">{{
                                                    t("To")
                                                }}</span>
                                                :
                                                {{
                                                    endDate
                                                        | date : "dd-MM-yyyy"
                                                }}
                                            </span>
                                        </div>

                                        <!-- Edit Date Filter Button -->
                                        <button
                                            mat-icon-button
                                            (click)="toggleDatePicker()"
                                            [matTooltip]="t('Edit date filter')"
                                        >
                                            <mat-icon
                                                class="text-3xl"
                                                [ngClass]="
                                                    startDate || endDate
                                                        ? 'text-gradient'
                                                        : ''
                                                "
                                            >
                                                today
                                            </mat-icon>
                                        </button>

                                        <!-- Filter Tracker Button -->
                                        <button
                                            mat-stroked-button
                                            (click)="openFilterDialog()"
                                            [matTooltip]="t('Filter Tracker')"
                                        >
                                            <mat-icon
                                                [ngClass]="
                                                    appliedFilters?.length > 0
                                                        ? 'text-gradient'
                                                        : ''
                                                "
                                            >
                                                filter_alt
                                            </mat-icon>
                                            <span>{{ t("Filters") }}</span>
                                            <span
                                                *ngIf="
                                                    appliedFilters?.length > 0
                                                "
                                                class="ml-2 px-2 py-0.5 bg-primary text-white rounded-full text-xs font-bold"
                                            >
                                                {{ appliedFilters.length }}
                                            </span>
                                        </button>
                                    </div>
                                </div>

                                <!-- Date Pickers (when editing) -->
                                <div
                                    *ngIf="showDatePicker"
                                    class="flex gap-2 items-center justify-end mb-4 flex-wrap"
                                >
                                    <mat-form-field
                                        appearance="fill"
                                        class="w-42 md:w-48 fuse-mat-dense"
                                    >
                                        <input
                                            matInput
                                            [matDatepicker]="picker1"
                                            [(ngModel)]="startDate"
                                            [placeholder]="t('Start')"
                                        />
                                        <mat-datepicker-toggle
                                            matSuffix
                                            [for]="picker1"
                                        ></mat-datepicker-toggle>
                                        <mat-datepicker
                                            #picker1
                                        ></mat-datepicker>
                                    </mat-form-field>

                                    <mat-form-field
                                        appearance="fill"
                                        class="w-42 md:w-48 fuse-mat-dense"
                                    >
                                        <input
                                            matInput
                                            [matDatepicker]="picker2"
                                            [(ngModel)]="endDate"
                                            [placeholder]="t('End')"
                                        />
                                        <mat-datepicker-toggle
                                            matSuffix
                                            [for]="picker2"
                                        ></mat-datepicker-toggle>
                                        <mat-datepicker
                                            #picker2
                                        ></mat-datepicker>
                                    </mat-form-field>

                                    <button
                                        mat-icon-button
                                        (click)="applyDateRange()"
                                    >
                                        <mat-icon class="text-xl text-gradient"
                                            >check</mat-icon
                                        >
                                    </button>
                                    <button
                                        mat-icon-button
                                        (click)="toggleDatePicker()"
                                    >
                                        <mat-icon class="text-xl"
                                            >close</mat-icon
                                        >
                                    </button>
                                </div>

                                <!-- Chart skeleton until chart data is loaded -->
                                <div
                                    *ngIf="chartLoading"
                                    class="chart-skeleton"
                                    aria-hidden="true"
                                >
                                    <div class="chart-skeleton__header">
                                        <div class="chart-skeleton__line chart-skeleton__line--title"></div>
                                        <div class="chart-skeleton__line chart-skeleton__line--sub"></div>
                                    </div>
                                    <div class="chart-skeleton__body">
                                        <div class="chart-skeleton__bars">
                                            <div class="chart-skeleton__bar" style="height: 45%"></div>
                                            <div class="chart-skeleton__bar" style="height: 70%"></div>
                                            <div class="chart-skeleton__bar" style="height: 55%"></div>
                                            <div class="chart-skeleton__bar" style="height: 85%"></div>
                                            <div class="chart-skeleton__bar" style="height: 60%"></div>
                                            <div class="chart-skeleton__bar" style="height: 75%"></div>
                                            <div class="chart-skeleton__bar" style="height: 40%"></div>
                                            <div class="chart-skeleton__bar" style="height: 90%"></div>
                                        </div>
                                        <div class="chart-skeleton__x-axis">
                                            <div class="chart-skeleton__line chart-skeleton__line--short"></div>
                                            <div class="chart-skeleton__line chart-skeleton__line--short"></div>
                                            <div class="chart-skeleton__line chart-skeleton__line--short"></div>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    *ngIf="!chartLoading && noTrackerData"
                                    class="flex flex-col items-center justify-center py-16 px-4 text-center"
                                >
                                    <mat-icon class="!text-5xl text-gray-400 mb-4">info_outline</mat-icon>
                                    <p class="text-base font-medium text-red-600">
                                        {{ t('This Tracker has no data available for the selected filter. Please try choosing a different filter.') }}
                                    </p>
                                </div>

                                <app-dynamic-charts
                                    #trackerChart
                                    *ngIf="!chartLoading && !noTrackerData"
                                    [chartData]="chartOptions"
                                    [isFull]="true"
                                    [from]="data1.from"
                                    [initialSortOrder]="data1.sortOrder"
                                    [previousSelectedSeriesList]="
                                        data1?.selectedSeriesList ??
                                        chartOptions?.selectedSeriesList ??
                                        []
                                    "
                                    [chartTitle]="
                                        chartOptions.xAxis
                                            ? chartOptions.xAxis
                                            : 'X-Axis' +
                                              ' vs ' +
                                              chartOptions.yAxis
                                            ? chartOptions.yAxis
                                            : 'Y-Axis'
                                    "
                                    [currentIndex]="
                                        currentIndex ? currentIndex : 0
                                    "
                                    (dateRangeChange)="
                                        onDateRangeChange($event)
                                    "
                                    (sortChange)="
                                        onSortChange($event)
                                    "
                                ></app-dynamic-charts>
                            </div>
                        </div>

                        <!-- Data Table Card -->
                        <div
                            class="fcp-card flex-1 min-w-0 lg:w-1/2"
                        >
                            <!-- Table Header -->
                            <div
                                class="fcp-table-head px-6 py-4"
                            >
                                <div
                                    class="flex items-center justify-between gap-3 flex-wrap"
                                >
                                    <h3
                                        class="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2"
                                    >
                                        <mat-icon class="text-2xl"
                                            >table_view</mat-icon
                                        >
                                        {{ t("Data Table") }}
                                    </h3>
                                    <button
                                        *ngIf="
                                            !tableLoading &&
                                            !tableError &&
                                            tableRows?.length > 0
                                        "
                                        type="button"
                                        mat-flat-button
                                        class="px-6 py-2 rounded-lg bg-primary"
                                        (click)="exportAsCsv()"
                                    >
                                        {{ t("Export as CSV") }}
                                    </button>
                                </div>
                            </div>

                            <!-- Table Content: single scroll container; nested overflow-x breaks sticky th in Chrome -->
                            <div
                                class="max-h-[500px] overflow-x-auto overflow-y-auto"
                            >
                                <!-- Loading State -->
                                <div
                                    *ngIf="tableLoading"
                                    class="flex flex-col items-center justify-center py-16 px-4"
                                >
                                    <mat-spinner diameter="48"></mat-spinner>
                                    <p class="mt-4 text-sm text-gray-500">
                                        {{ t("Loading table data") }}...
                                    </p>
                                </div>

                                <!-- Error State -->
                                <div
                                    *ngIf="!tableLoading && tableError"
                                    class="flex flex-col items-center justify-center py-16 px-4 text-center"
                                >
                                    <mat-icon
                                        class="!text-5xl text-red-500 mb-4"
                                        >error_outline</mat-icon
                                    >
                                    <p
                                        class="text-base font-medium text-red-600"
                                    >
                                        {{ tableError }}
                                    </p>
                                </div>

                                <!-- Empty State -->
                                <div
                                    *ngIf="
                                        !tableLoading &&
                                        !tableError &&
                                        tableRows?.length === 0
                                    "
                                    class="flex flex-col items-center justify-center py-16 px-4 text-center"
                                >
                                    <mat-icon
                                        class="!text-5xl text-gray-400 mb-4"
                                        >inbox</mat-icon
                                    >
                                    <p
                                        class="text-base font-medium text-gray-500"
                                    >
                                        {{ t("No data available") }}
                                    </p>
                                </div>

                                <!-- Table -->
                                <table
                                    *ngIf="
                                        !tableLoading &&
                                        !tableError &&
                                        tableRows?.length > 0
                                    "
                                    class="w-full min-w-max border-separate border-spacing-0 text-sm"
                                >
                                        <thead>
                                            <tr>
                                                <th
                                                    *ngFor="
                                                        let c of tableColumns
                                                    "
                                                    class="sticky top-0 z-20 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 px-6 py-4 text-left text-xs font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300 whitespace-nowrap shadow-[0_1px_0_0_rgb(229_231_235)] dark:shadow-[0_1px_0_0_rgb(55_65_81)]"
                                                >
                                                    {{ c }}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
                                            <tr
                                                *ngFor="
                                                    let r of tableRows;
                                                    let i = index
                                                "
                                                class="transition-colors hover:bg-gray-50 dark:hover:bg-gray-700"
                                                [ngClass]="{
                                                    'bg-white dark:bg-gray-800': i % 2 === 0,
                                                    'bg-gray-50 dark:bg-gray-700': i % 2 !== 0
                                                }"
                                            >
                                                <td
                                                    *ngFor="
                                                        let c of tableColumns
                                                    "
                                                    [appCopyOnDblclick]="r[c]"
                                                    class="px-6 py-4 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap"
                                                    matTooltipPosition="above"
                                                    [matTooltip]="
                                                        t('Double-click to copy')
                                                    "
                                                >
                                                    {{ r[c] | formatting : c }}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                            </div>
                        </div>
                    </div>
                </ng-container>

                <!-- Non-Tracker Content -->
                <ng-template #defaultFull>
                    <div class="fcp-card">

                        <div class="p-6">
                            <app-dynamic-charts
                                [chartData]="chartOptions"
                                [isFull]="true"
                                [from]="data1.from"
                                [chartTitle]="
                                    chartOptions.xAxis
                                        ? chartOptions.xAxis
                                        : 'X-Axis' + ' vs ' + chartOptions.yAxis
                                        ? chartOptions.yAxis
                                        : 'Y-Axis'
                                "
                                [currentIndex]="currentIndex ? currentIndex : 0"
                                (dateRangeChange)="onDateRangeChange($event)"
                            ></app-dynamic-charts>
                        </div>
                    </div>
                </ng-template>
            </div>
        </div>
    </div>
</ng-container>
